import { useState } from "react";
import { useUser } from "@/contexts/UserContext";
import { useAdminCustomization } from "@/hooks/useAdminCustomization";
import { useClientProviderEvaluations, useUpdateProviderEvaluation, calculateAverageScore, type ShiftaskProviderEvaluation } from "@/hooks/useShiftaskProviderEvaluation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Download, Calendar, Star, MessageSquare } from "lucide-react";
import { generateProviderEvaluationPDF } from "@/utils/generateProviderEvaluationPDF";
import { format } from "date-fns";
import { el } from "date-fns/locale";

const metrics = [
  { key: 'response_time', labelEl: 'Χρόνος Απόκρισης' },
  { key: 'incident_handling', labelEl: 'Διαχείριση Συμβάντων' },
  { key: 'guard_quality', labelEl: 'Ποιότητα Φρουρών' },
  { key: 'professionalism', labelEl: 'Επαγγελματισμός' },
  { key: 'communication', labelEl: 'Επικοινωνία' },
  { key: 'technology_satisfaction', labelEl: 'Ικανοποίηση από Τεχνολογία' },
  { key: 'reporting_quality', labelEl: 'Ποιότητα Αναφορών' },
  { key: 'innovation', labelEl: 'Καινοτομία' },
  { key: 'value_for_money', labelEl: 'Αξία για Χρήματα' },
  { key: 'contract_flexibility', labelEl: 'Ευελιξία Συμβολαίου' },
  { key: 'account_management', labelEl: 'Διαχείριση Λογαριασμού' },
];

export const ClientProviderEvaluationsView = () => {
  const { user } = useUser();
  const { customization } = useAdminCustomization();
  const { data: evaluations = [] } = useClientProviderEvaluations(user?.id || '');
  const updateMutation = useUpdateProviderEvaluation();
  const appName = customization?.app_name || 'Provider';

  const currentYear = new Date().getFullYear();
  const currentQuarter = `Q${Math.floor(new Date().getMonth() / 3) + 1}`;
  
  const currentQuarterEvaluations = evaluations.filter(
    e => e.evaluation_year === currentYear && e.evaluation_period === currentQuarter
  );

  const [selectedEvaluation, setSelectedEvaluation] = useState<ShiftaskProviderEvaluation | null>(
    currentQuarterEvaluations[0] || null
  );
  const [clientComments, setClientComments] = useState<Record<string, string>>({});

  const handleDownloadPDF = async (evaluation: ShiftaskProviderEvaluation) => {
    await generateProviderEvaluationPDF(evaluation, user?.email || 'Client', appName, true);
  };

  const handleSaveComments = async () => {
    if (!selectedEvaluation) return;
    
    await updateMutation.mutateAsync({
      id: selectedEvaluation.id,
      updates: { client_mitigation_comments: clientComments }
    });
  };

  if (currentQuarterEvaluations.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">Δεν υπάρχουν αξιολογήσεις για το τρέχον τρίμηνο</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
        <CardHeader>
          <CardTitle className="text-2xl">Οι Αξιολογήσεις μου - {appName}</CardTitle>
          <CardDescription>
            {currentQuarter} {currentYear} - {currentQuarterEvaluations.length} υποβολές
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid gap-4">
        {currentQuarterEvaluations.map((evaluation) => {
          const avgScore = calculateAverageScore(evaluation);
          const isSelected = selectedEvaluation?.id === evaluation.id;
          
          return (
            <Card 
              key={evaluation.id}
              className={`cursor-pointer transition-all ${isSelected ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setSelectedEvaluation(evaluation)}
            >
              <CardContent className="flex items-center justify-between p-6">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary flex items-center gap-2">
                      <Star className="w-6 h-6 fill-primary" />
                      {avgScore.toFixed(1)}
                    </div>
                    <Label className="text-xs text-muted-foreground">Μέσος Όρος</Label>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">
                        {format(new Date(evaluation.submitted_at), 'PPp', { locale: el })}
                      </span>
                    </div>
                    {evaluation.mitigation_shared_at && (
                      <Badge variant="secondary">
                        <MessageSquare className="w-3 h-3 mr-1" />
                        Απάντηση από {appName}
                      </Badge>
                    )}
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadPDF(evaluation);
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {selectedEvaluation && (
        <Tabs defaultValue="analytics" className="w-full">
          <TabsList className="w-full">
            <TabsTrigger value="analytics" className="flex-1">Αναλυτικά</TabsTrigger>
            <TabsTrigger value="mitigations" className="flex-1">
              Απαντήσεις {appName}
              {selectedEvaluation.mitigation_shared_at && (
                <Badge variant="secondary" className="ml-2">Νέα</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analytics" className="space-y-4 mt-6">
            {metrics.map((metric) => {
              const score = selectedEvaluation[`${metric.key}_score` as keyof ShiftaskProviderEvaluation] as number;
              const comments = selectedEvaluation[`${metric.key}_comments` as keyof ShiftaskProviderEvaluation] as string;
              
              return (
                <Card key={metric.key}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">{metric.labelEl}</CardTitle>
                      <Badge variant={score >= 4 ? 'default' : score >= 3 ? 'secondary' : 'destructive'}>
                        {score}/5
                      </Badge>
                    </div>
                  </CardHeader>
                  {comments && (
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{comments}</p>
                    </CardContent>
                  )}
                </Card>
              );
            })}

            <Card>
              <CardHeader>
                <CardTitle>Γενική Ικανοποίηση</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Συνολική Βαθμολογία</Label>
                  <Badge variant="default" className="text-lg">
                    {selectedEvaluation.overall_satisfaction}/5
                  </Badge>
                </div>

                {selectedEvaluation.what_working_well && (
                  <div>
                    <Label className="text-sm font-medium">Τι λειτουργεί καλά</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {selectedEvaluation.what_working_well}
                    </p>
                  </div>
                )}

                {selectedEvaluation.improvement_areas && (
                  <div>
                    <Label className="text-sm font-medium">Περιοχές βελτίωσης</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {selectedEvaluation.improvement_areas}
                    </p>
                  </div>
                )}

                {selectedEvaluation.additional_comments && (
                  <div>
                    <Label className="text-sm font-medium">Πρόσθετα σχόλια</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {selectedEvaluation.additional_comments}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="mitigations" className="space-y-4 mt-6">
            {selectedEvaluation.mitigation_shared_at ? (
              <div className="space-y-4">
                <Card className="bg-primary/5">
                  <CardHeader>
                    <CardTitle className="text-base">Απαντήσεις από {appName}</CardTitle>
                    <CardDescription>
                      Μοιράστηκε στις {format(new Date(selectedEvaluation.mitigation_shared_at), 'PPp', { locale: el })}
                    </CardDescription>
                  </CardHeader>
                </Card>

                {metrics.map((metric) => {
                  const mitigation = selectedEvaluation.admin_mitigations?.[metric.key];
                  if (!mitigation) return null;

                  return (
                    <Card key={metric.key}>
                      <CardHeader>
                        <CardTitle className="text-base">{metric.labelEl}</CardTitle>
                        <CardDescription className="text-sm">
                          Σχέδιο Δράσης από {appName}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="bg-primary/10 p-4 rounded-lg">
                          <p className="text-sm">{mitigation}</p>
                        </div>

                        <div className="space-y-2">
                          <Label>Η δική σας ανατροφοδότηση</Label>
                          <Textarea
                            placeholder="Προσθέστε τα σχόλιά σας..."
                            value={clientComments[metric.key] || selectedEvaluation.client_mitigation_comments?.[metric.key] || ''}
                            onChange={(e) => setClientComments({
                              ...clientComments,
                              [metric.key]: e.target.value
                            })}
                            className="min-h-[80px]"
                          />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}

                <Button 
                  onClick={handleSaveComments}
                  disabled={updateMutation.isPending}
                  className="w-full"
                >
                  {updateMutation.isPending ? 'Αποθήκευση...' : 'Αποθήκευση Σχολίων'}
                </Button>
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">
                    Ο {appName} δεν έχει μοιραστεί ακόμα σχέδια δράσης για αυτήν την αξιολόγηση
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};
